import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { toast } from "react-toastify";
import { GrNext, GrPrevious } from "react-icons/gr";
import {
  TbAlertTriangle,
  TbCircleCheck,
  TbClock,
  TbDownload,
  TbEyeSearch,
  TbFileDescription,
  TbPackage,
  TbRefresh,
  TbSearch,
  TbUser,
  TbBuildingFactory2,
} from "react-icons/tb";
import api from "../api/axios";
import { dateTimeLocalToIso, toDateTimeLocalValue } from "../utils/dateTime";
import "../assets/styles/production-management.css";
import "../assets/styles/directory-tables.css";

const PAGE_NAME = "Orders";
const PAGE_SIZE = 10;
const headers = (action) => ({ "x-page-name": PAGE_NAME, "x-action": action });

const STATUS_OPTIONS = [
  { value: "", label: "All jobs" },
  { value: "queued", label: "Queued" },
  { value: "working", label: "Working" },
  { value: "awaiting_client", label: "Additional client requirement" },
  { value: "client_changes", label: "Client changes" },
  { value: "deadline_missed", label: "Deadline missed" },
  { value: "on_hold", label: "On hold" },
  { value: "completed", label: "Completed" },
];

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [...new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1
      ? [`ellipsis-${page}`, page]
      : [page]
  );
};

const UPDATE_TYPES = [
  { value: "note", label: "Work note" },
  { value: "client_requirement", label: "Additional client requirement" },
  { value: "client_change", label: "Client change" },
  { value: "deadline_change", label: "Deadline note" },
];

const labelForStatus = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status)?.label ||
  String(status || "Queued").replace(/_/g, " ");

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isOverdue = (job) =>
  job?.work_status !== "completed" &&
  job?.target_completion_at &&
  new Date(job.target_completion_at).getTime() < Date.now();

const getProgress = (job) => {
  if (job.work_status === "completed") return 100;
  const start = new Date(job.started_at || job.createdAt).getTime();
  const end = new Date(job.target_completion_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.max(4, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
};

const getProductSnapshot = (item) => item.product_snapshot || {};

const ProductionManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [updateForm, setUpdateForm] = useState({ update_type: "note", title: "", details: "" });
  const [vendors, setVendors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

  const showError = (error, fallback) => {
    if (error.response?.status !== 403) toast.error(error.response?.data?.message || fallback);
  };

  const fetchJobs = async (page = 1, selectedStatus = status, term = search) => {
    try {
      setLoading(true);
      const response = await api.get("/admin/orders/production", {
        params: { page, limit: PAGE_SIZE, status: selectedStatus || undefined, search: term.trim() || undefined },
        headers: headers("view"),
      });
      setJobs(response.data?.data || []);
      setPagination(response.data?.pagination || { page, total: 0, totalPages: 1 });
    } catch (error) {
      showError(error, "Failed to load production jobs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(1, status, search);
    // Search is submitted explicitly to avoid a request on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    api.get("/admin/orders/vendors", {
      params: { page: 1, limit: 100 },
      headers: headers("view"),
    }).then((response) => setVendors(response.data?.data || []))
      .catch((error) => showError(error, "Failed to load printing vendors."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(
    () => ({
      active: jobs.filter((job) => !["completed", "on_hold"].includes(job.work_status)).length,
      overdue: jobs.filter(isOverdue).length,
      changes: jobs.filter((job) => ["awaiting_client", "client_changes"].includes(job.work_status)).length,
      complete: jobs.filter((job) => job.work_status === "completed").length,
    }),
    [jobs]
  );

  const openJob = (job) => {
    setSelectedJob(job);
    setEditForm({
      assigned_to: job.assigned_to || "",
      assignee_contact: job.assignee_contact || "",
      target_completion_at: toDateTimeLocalValue(job.target_completion_at),
      priority: job.priority || "normal",
      work_status: job.work_status || "working",
      production_brief: job.production_brief || "",
      vendor_id: job.vendor_id || "",
      change_note: "",
    });
    setSidebarOpen(true);
  };

  const replaceJob = (job) => {
    setSelectedJob(job);
    setJobs((current) => current.map((item) => (item.production_job_id === job.production_job_id ? job : item)));
  };

  const saveJob = async () => {
    if (!editForm.assigned_to?.trim()) return toast.error("Assigned team member is required.");
    if (!editForm.target_completion_at) return toast.error("Completion deadline is required.");
    try {
      setSaving(true);
      const response = await api.put(
        `/admin/orders/${selectedJob.order_id}/production`,
        {
          ...editForm,
          assigned_to: editForm.assigned_to.trim(),
          target_completion_at: dateTimeLocalToIso(editForm.target_completion_at),
        },
        { headers: headers("edit") }
      );
      replaceJob(response.data?.data);
      setEditForm((current) => ({ ...current, change_note: "" }));
      toast.success("Production job updated.");
    } catch (error) {
      showError(error, "Could not update the production job.");
    } finally {
      setSaving(false);
    }
  };

  const addUpdate = async () => {
    if (!updateForm.title.trim()) return toast.error("Add a short update title.");
    try {
      setSaving(true);
      const response = await api.post(
        `/admin/orders/${selectedJob.order_id}/production/updates`,
        updateForm,
        { headers: headers("edit") }
      );
      replaceJob(response.data?.data);
      setUpdateForm({ update_type: "note", title: "", details: "" });
      toast.success("Timeline update added.");
    } catch (error) {
      showError(error, "Could not add the timeline update.");
    } finally {
      setSaving(false);
    }
  };

  const downloadJobCard = async () => {
    try {
      setDownloading(true);
      const response = await api.get(`/admin/orders/${selectedJob.order_id}/job-card`, {
        responseType: "blob",
        headers: headers("view"),
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `job-card-${selectedJob.order?.order_number || selectedJob.order_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Job card downloaded.");
    } catch (error) {
      showError(error, "Could not download the job card.");
    } finally {
      setDownloading(false);
    }
  };

  const renderRequirements = (item) => {
    const options = (Array.isArray(item.selected_options) ? item.selected_options : [])
      .slice()
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const values = (item.form_values || [])
      .slice()
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    if (!options.length && !values.length) return <small>No custom requirements.</small>;
    return (
      <div className="production-product-requirements">
        {options.length > 0 && (
          <div className="production-requirement-group">
            <strong>Selected Options</strong>
            {options.map((option, index) => (
              <p key={`${option.field_id || "option"}-${option.value_id || index}`}>
                <b>{option.field_name || option.template_name || "Option"}:</b>{" "}
                {option.label || option.value || "-"}
                {Number(option.price_adjustment || 0) > 0
                  ? ` (+${formatCurrency(option.price_adjustment).replace(".00", "")})`
                  : ""}
              </p>
            ))}
          </div>
        )}
        {values.length > 0 && (
          <div className="production-requirement-group submitted">
            <strong>Submitted Details</strong>
            {values.map((field) => (
              <p key={field.order_item_form_value_id}>
                <b>{field.field_label || field.field_name}:</b>{" "}
                {field.input_type === "file" && field.file_url ? (
                  <a href={field.file_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                    {field.file_name || "View file"}
                  </a>
                ) : (
                  field.value || field.file_name || "-"
                )}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="admin-page production-management-page">
      <div className="page-header users-hero-header production-hero">
        <div>
          <p>Assignments, deadlines and client changes</p>
          <h1>Production Management</h1>
        </div>
        <button type="button" className="primary-action" onClick={() => fetchJobs(1)} disabled={loading}>
          <TbRefresh /> {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="production-summary-grid">
        <div><TbClock /><span><strong>{totals.active}</strong>Active on this page</span></div>
        <div className="danger"><TbAlertTriangle /><span><strong>{totals.overdue}</strong>Past deadline</span></div>
        <div className="warning"><TbUser /><span><strong>{totals.changes}</strong>Client action</span></div>
        <div className="success"><TbCircleCheck /><span><strong>{totals.complete}</strong>Completed</span></div>
      </div>

      <div className="page-card production-board-card">
        <div className="production-toolbar">
          <div className="premium-search-box">
            <TbSearch />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && fetchJobs(1, status, search)}
              placeholder="Search order number or assignee..."
            />
            <button type="button" onClick={() => fetchJobs(1, status, search)}>Search</button>
          </div>
          <select
            className="production-status-filter"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter production jobs by status"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table production-data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Assigned to</th>
                <th>Vendor</th>
                <th>Deadline</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Products</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && jobs.length === 0 && (
                <tr>
                  <td className="directory-table-empty" colSpan="9">
                    <TbPackage />
                    <strong>No production jobs found</strong>
                    <span>Move a confirmed order to Processing to create its job.</span>
                  </td>
                </tr>
              )}

              {jobs.map((job) => {
                const overdue = isOverdue(job);
                const progress = getProgress(job);

                return (
                  <tr className={overdue ? "production-overdue-row" : ""} key={job.production_job_id}>
                    <td className="directory-person-cell">
                      <div className="directory-person">
                        <span className="directory-avatar"><TbPackage /></span>
                        <div>
                          <strong>{job.order?.order_number || `Order #${job.order_id}`}</strong>
                          <span>{job.order?.customer_snapshot?.name || "Customer"}</span>
                          <small>Job #{job.production_job_id}</small>
                        </div>
                      </div>
                    </td>

                    <td className="production-assignee-cell">
                      <strong>{job.assigned_to || "Unassigned"}</strong>
                      <span>{job.assignee_contact || "No contact"}</span>
                    </td>

                    <td className="production-assignee-cell">
                      <strong>{job.vendor?.name || "Not assigned"}</strong>
                      <span>{job.vendor?.vendor_code || "Required at printing"}</span>
                    </td>

                    <td className="directory-date-cell">
                      <strong>{formatDate(job.target_completion_at, true)}</strong>
                      <span>{overdue ? "Past deadline" : "Target completion"}</span>
                    </td>

                    <td>
                      <span className={`production-priority ${job.priority || "normal"}`}>
                        {job.priority || "normal"}
                      </span>
                    </td>

                    <td>
                      <span className={`production-status ${overdue ? "deadline_missed" : job.work_status}`}>
                        {overdue ? "Deadline missed" : labelForStatus(job.work_status)}
                      </span>
                    </td>

                    <td className="production-progress-cell">
                      <div><span>Timeline used</span><strong>{progress}%</strong></div>
                      <div className="production-progress">
                        <span style={{ width: `${progress}%` }} />
                      </div>
                    </td>

                    <td className="production-products-cell">
                      <strong>{job.order?.items?.length || 0}</strong>
                      <span>product(s)</span>
                    </td>

                    <td className="directory-actions-cell">
                      <div className="directory-row-actions">
                        <button
                          type="button"
                          title="Manage production job"
                          aria-label={`Manage production job ${job.order?.order_number || job.production_job_id}`}
                          onClick={() => openJob(job)}
                        >
                          <TbEyeSearch />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button type="button" className="directory-pagination-direction" disabled={pagination.page <= 1 || loading} onClick={() => fetchJobs(pagination.page - 1)}><GrPrevious /> Previous</button>
          <div className="directory-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map((item) =>
              typeof item === "string" ? (
                <span key={item}>…</span>
              ) : (
                <button
                  type="button"
                  className={item === pagination.page ? "active" : ""}
                  key={item}
                  disabled={loading}
                  aria-label={`Go to page ${item}`}
                  aria-current={item === pagination.page ? "page" : undefined}
                  onClick={() => fetchJobs(item)}
                >
                  {item}
                </button>
              )
            )}
          </div>
          <button type="button" className="directory-pagination-direction" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => fetchJobs(pagination.page + 1)}>Next <GrNext /></button>
        </div>
      </div>

      <Sidebar visible={sidebarOpen} position="right" onHide={() => setSidebarOpen(false)} className="production-workspace-sidebar">
        {selectedJob && (
          <div className="production-workspace">
            <div className="production-workspace-title">
              <span>Production workspace</span>
              <h2>{selectedJob.order?.order_number}</h2>
              <p>{selectedJob.order?.customer_snapshot?.name || "Customer"}</p>
              <button type="button" className="job-card-download" onClick={downloadJobCard} disabled={downloading}>
                <TbDownload /> {downloading ? "Preparing PDF..." : "Download Job Card"}
              </button>
            </div>

            {selectedJob.order?.questionnaire && (
              <div className="production-questionnaire-shortcut">
                <TbFileDescription />
                <div>
                  <strong>Wedding questionnaire attached</strong>
                  <span>
                    Status: {selectedJob.order.questionnaire.status || "pending"}.
                    Its supplied answers are included in the downloaded job card.
                  </span>
                </div>
                <Link
                  to={`/questionnaires?search=${encodeURIComponent(
                    selectedJob.order?.order_number || ""
                  )}`}
                >
                  View response
                </Link>
              </div>
            )}

            <div className="production-workspace-section">
              <div className="section-title"><div><span>01</span><h3>Ownership & schedule</h3></div><small>Keep the live job details current.</small></div>
              <div className="production-edit-grid">
                <label>Assigned to<input value={editForm.assigned_to || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, assigned_to: event.target.value }))} /></label>
                <label>Contact<input value={editForm.assignee_contact || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, assignee_contact: event.target.value }))} /></label>
                <label>Deadline<input type="datetime-local" value={editForm.target_completion_at || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, target_completion_at: event.target.value }))} /></label>
                <label>Priority<select value={editForm.priority || "normal"} onChange={(event) => setEditForm((prev) => ({ ...prev, priority: event.target.value }))}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
                <label className="wide">Printing vendor<select value={editForm.vendor_id || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, vendor_id: event.target.value }))}><option value="">Not assigned yet</option>{vendors.map((vendor) => <option key={vendor.vendor_id} value={vendor.vendor_id} disabled={vendor.status !== "active" && Number(vendor.vendor_id) !== Number(editForm.vendor_id)}>{vendor.name} ({vendor.vendor_code}){vendor.status !== "active" ? " - inactive" : ""}</option>)}</select><small><TbBuildingFactory2 /> Vendor details stay internal and are never shown to the customer.</small></label>
                <label className="wide">Current status<select value={editForm.work_status || "working"} onChange={(event) => setEditForm((prev) => ({ ...prev, work_status: event.target.value }))}>{STATUS_OPTIONS.filter((item) => item.value).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
                <label className="wide">Production brief<textarea rows="4" value={editForm.production_brief || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, production_brief: event.target.value }))} /></label>
                <label className="wide">Change note<textarea rows="2" value={editForm.change_note || ""} onChange={(event) => setEditForm((prev) => ({ ...prev, change_note: event.target.value }))} placeholder="Optional context for the timeline" /></label>
              </div>
              <button type="button" className="primary-action production-save-button" onClick={saveJob} disabled={saving}>{saving ? "Saving..." : "Save Job Changes"}</button>
            </div>

            <div className="production-workspace-section">
              <div className="section-title"><div><span>02</span><h3>Order products & requirements</h3></div></div>
              <div className="production-products">
                {(selectedJob.order?.items || []).map((item) => {
                  const product = getProductSnapshot(item);
                  return <article key={item.order_item_id}>
                    <img src={product.image || "/img/noimage.jpg"} alt={product.name || "Product"} />
                    <div>
                      <h4>{product.name || product.product_name || "Product"}</h4>
                      <div className="production-product-facts">
                        <span className="production-delivery-badge">{item.delivery_type === "physical" ? "Printable" : "Digital"}</span>
                        <span><b>SKU:</b> {product.sku || "-"}</span>
                        <span><b>Qty:</b> {item.quantity}</span>
                        <span><b>Total:</b> {formatCurrency(item.total_price)}</span>
                      </div>
                      {renderRequirements(item)}
                    </div>
                  </article>;
                })}
              </div>
            </div>

            <div className="production-workspace-section">
              <div className="section-title"><div><span>03</span><h3>Add timeline update</h3></div><small>Record requirements, changes and work notes.</small></div>
              <div className="production-update-form">
                <select value={updateForm.update_type} onChange={(event) => setUpdateForm((prev) => ({ ...prev, update_type: event.target.value }))}>{UPDATE_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                <input value={updateForm.title} onChange={(event) => setUpdateForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Short update title" />
                <textarea rows="3" value={updateForm.details} onChange={(event) => setUpdateForm((prev) => ({ ...prev, details: event.target.value }))} placeholder="Details, client instruction or next action..." />
                <button type="button" className="primary-action" onClick={addUpdate} disabled={saving}>Add to Timeline</button>
              </div>
            </div>

            <div className="production-workspace-section">
              <div className="section-title"><div><span>04</span><h3>Job timeline</h3></div></div>
              <div className="production-timeline">
                {(selectedJob.updates || []).map((update) => (
                  <div className={`production-timeline-entry ${update.update_type}`} key={update.production_update_id}>
                    <i />
                    <div><small>{formatDate(update.createdAt, true)} • {update.created_by || "Admin"}</small><h4>{update.title}</h4>{update.details && <p>{update.details}</p>}</div>
                  </div>
                ))}
                {!selectedJob.updates?.length && <p className="production-empty-timeline">No updates recorded yet.</p>}
              </div>
            </div>
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default ProductionManagement;
